import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { ElementChangedFlags, UndoOperation } from '@egr/wcf/modules/core/mdl';
import { quaternionToAxisAngle, rotateVector3ToRef, VECTOR3_X, VECTOR3_Z } from "@egr/wcf/modules/core/utils/MathUtils";
import { clone as arrClone } from '@egr/wcf/modules/utils/array';
import { rotate } from '@egr/wcf/modules/core/utils/MeshUtils';
import { updateFrozenWorldMatrix } from '@egr/wcf/modules/core/utils/MeshUtils';
import { Util } from "../utils/Util";
import { AbstractRoomCommand } from "./AbstractRoomCommand";
export class AlignElements extends AbstractRoomCommand {
    constructor(pRoomManager) {
        super(pRoomManager);
        this.mRoomManager = pRoomManager;
    }
    initialize(pElements) {
        this.mElements = arrClone(pElements);
        //store parent world matrix and transforms for undo
        this.mUndoTransforms = new Array();
        this.mUndoData = new Array();
        this.mInvWorldMatrix = new Array();
        this.mInvWorldRotation = new Array();
        //this.mApp.model.eventBeginTransformElements.trigger();
        for (let tElement of this.mElements) {
            this.mUndoTransforms.push(tElement.transform.clone());
            this.mUndoData.push(tElement.saveUndoData(UndoOperation.Transform));
            if (tElement.node.parent != null) {
                let tMat = tElement.node.parent.getWorldMatrix().clone().invert();
                this.mInvWorldMatrix.push(tMat);
                let tPos = Vector3.Zero();
                let tRot = new Quaternion();
                let tScale = new Vector3(1, 1, 1);
                tMat.decompose(tScale, tRot, tPos);
                this.mInvWorldRotation.push(tRot);
            }
            else {
                this.mInvWorldMatrix.push(null);
                this.mInvWorldRotation.push(null);
            }
        }
    }
    isAvailable() {
        return (this.mElements != null);
    }
    isUndoable() {
        return (true);
    }
    execute(pParameter) {
        this.mRoomManager.app.commands.beginUndoSection();
        //notify elements that transformation has changed
        this.mApp.model.beginModification();
        for (let tElement of this.mElements) {
            tElement.notifyChange(ElementChangedFlags.Transformation);
        }
        //this.mApp.model.eventEndTransformElements.trigger();
        this.mApp.model.endModification();
        //store transforms for redo
        this.mRedoTransforms = new Array(this.mElements.length);
        this.mRedoData = new Array();
        for (let i = 0; i < this.mElements.length; ++i) {
            this.mRedoTransforms[i] = this.mElements[i].transform.clone();
            this.mRedoData[i] = this.mElements[i].saveUndoData(UndoOperation.Transform);
        }
        this.mRoomManager.app.commands.endUndoSection();
        return (Promise.resolve(true));
    }
    cancel() {
        //
    }
    undo() {
        //this.mApp.model.eventBeginTransformElements.trigger();
        this.undoTransforms();
        return (Promise.resolve());
    }
    redo() {
        this.mApp.model.beginModification();
        //this.mApp.model.eventBeginTransformElements.trigger();
        for (let i = 0; i < this.mElements.length; ++i) {
            this.mElements[i].setTransform(this.mRedoTransforms[i]);
        }
        for (let i = 0; i < this.mElements.length; ++i) {
            if (this.mRedoData[i] != null)
                this.mElements[i].loadUndoData(UndoOperation.Transform, this.mRedoData[i]);
        }
        //this.mApp.model.eventEndTransformElements.trigger();
        this.mApp.model.endModification();
        this.mApp.viewer.requestRenderFrame();
        return (Promise.resolve());
    }
    /**
     * pTranslation is given in world coordinates.
     */
    translate(pElement_Index, pTranslation) {
        let tTranslation = pTranslation;
        if (this.mInvWorldMatrix[pElement_Index] != null)
            tTranslation = Vector3.TransformNormal(tTranslation, this.mInvWorldMatrix[pElement_Index]);
        this.mElements[pElement_Index].node.position.addInPlace(tTranslation);
        updateFrozenWorldMatrix(this.mElements[pElement_Index].node);
        this.mApp.rendering.notifyModelInteraction();
        this.mApp.viewer.requestRenderFrame();
    }
    getSortedOrder_X(pSel) {
        let tRes = new Array();
        if (pSel.length == 0)
            return (tRes);
        // get max/min of x axis (center of element bounding boxes)
        let tminX = this.getBBcenter_minX(pSel);
        tminX.z = 0;
        tminX.y = 0;
        let tmaxX = this.getBBcenter_maxX(pSel);
        tmaxX.z = 0;
        tmaxX.y = 0;
        tRes = this.getPositionX_order(tminX, pSel);
        return (tRes);
    }
    getSortedOrder_Z(pSel) {
        let tRes = new Array();
        if (pSel.length == 0)
            return (tRes);
        // get max/min of z axis (center of element bounding boxes)
        let tminZ = this.getBBcenter_minZ(pSel);
        tminZ.x = 0;
        tminZ.y = 0;
        let tmaxZ = this.getBBcenter_maxZ(pSel);
        tmaxZ.x = 0;
        tmaxZ.y = 0;
        tRes = this.getPositionZ_order(tminZ, pSel);
        return (tRes);
    }
    distribute_V() {
        let tSel = this.mElements;
        if (tSel.length == 0)
            return;
        // get max/min of z axis (center of element bounding boxes)
        let tminZ = this.getBBcenter_minZ(tSel);
        tminZ.x = 0;
        tminZ.y = 0;
        let tmaxZ = this.getBBcenter_maxZ(tSel);
        tmaxZ.x = 0;
        tmaxZ.y = 0;
        const tDist = Vector3.Distance(tminZ, tmaxZ);
        const tGap = tDist / (tSel.length - 1);
        let tnextZPos = tminZ.z + tGap;
        let tsortedElements = this.getPositionZ_order(tminZ, tSel);
        for (let i = 1; i < tsortedElements.length - 1; i++) {
            const tWM = tsortedElements[i].node.computeWorldMatrix();
            const tnodePos = tsortedElements[i].node.position.clone();
            const tBBcenter = tsortedElements[i].boundingBoxVisible.getCenter().clone();
            const tBBcenterTransformed = Vector3.TransformCoordinates(tBBcenter, tWM);
            const tposDiff = tBBcenterTransformed.subtract(tnodePos);
            let tnewPos = tBBcenterTransformed.clone();
            tnewPos.z = tnextZPos;
            tnewPos.subtractInPlace(tposDiff);
            tnewPos.y = tnodePos.y;
            tsortedElements[i].node.position = tnewPos;
            tsortedElements[i].updateTransformation();
            tnextZPos += tGap;
        }
        this.mApp.rendering.notifyModelInteraction();
        this.mApp.viewer.requestRenderFrame();
    }
    distribute_H() {
        let tSel = this.mElements;
        if (tSel.length == 0)
            return;
        // get max/min of x axis (center of element bounding boxes)
        let tminX = this.getBBcenter_minX(tSel);
        tminX.z = 0;
        tminX.y = 0;
        let tmaxX = this.getBBcenter_maxX(tSel);
        tmaxX.z = 0;
        tmaxX.y = 0;
        let tDist = Vector3.Distance(tminX, tmaxX);
        let tGap = tDist / (tSel.length - 1);
        let tnextXPos = tminX.x + tGap;
        let tsortedElements = this.getPositionX_order(tminX, tSel);
        for (let i = 1; i < tsortedElements.length - 1; i++) {
            const tWM = tsortedElements[i].node.computeWorldMatrix();
            const tnodePos = tsortedElements[i].node.position.clone();
            const tBBcenter = tsortedElements[i].boundingBoxVisible.getCenter().clone();
            const tBBcenterTransformed = Vector3.TransformCoordinates(tBBcenter, tWM);
            const tposDiff = tBBcenterTransformed.subtract(tnodePos);
            let tnewPos = tBBcenterTransformed.clone();
            tnewPos.x = tnextXPos;
            tnewPos.subtractInPlace(tposDiff);
            tnewPos.y = tnodePos.y;
            tsortedElements[i].node.position = tnewPos;
            tsortedElements[i].updateTransformation();
            tnextXPos += tGap;
        }
        this.mApp.rendering.notifyModelInteraction();
        this.mApp.viewer.requestRenderFrame();
    }
    CenterVertical() {
        let tSel = this.mElements;
        if (tSel.length == 0)
            return;
        // get center (z coordinate) point
        let tcenterX = this.getBB_centerZ(tSel);
        for (let i = 0; i < this.mElements.length; i++) {
            if (tcenterX != null) {
                let tintersect_maxZ = this.getBB_intersect_centerZ(this.mElements[i], tcenterX);
                if (tintersect_maxZ != null)
                    this.translate(i, tintersect_maxZ);
            }
        }
    }
    CenterHorizontal() {
        let tSel = this.mElements;
        if (tSel.length == 0)
            return;
        // get center (x coordinate) point
        let tcenterX = this.getBB_centerX(tSel);
        for (let i = 0; i < this.mElements.length; i++) {
            if (tcenterX != null) {
                let tintersect_maxX = this.getBB_intersect_centerX(this.mElements[i], tcenterX);
                if (tintersect_maxX != null)
                    this.translate(i, tintersect_maxX);
            }
        }
    }
    align_Left() {
        let tSel = this.mElements;
        if (tSel.length == 0)
            return;
        // get leftmost (x coordinate) point
        let tminX = this.getBB_minX(tSel);
        for (let i = 0; i < this.mElements.length; i++) {
            if (tminX != null) {
                let tintersect_maxX = this.getBB_intersect_minX(this.mElements[i], tminX);
                if (tintersect_maxX != null)
                    this.translate(i, tintersect_maxX);
            }
        }
    }
    align_Right() {
        let tSel = this.mElements;
        if (tSel.length == 0)
            return;
        // get rightmost (x coordinate) point
        let tmaxX = this.getBB_maxX(tSel);
        for (let i = 0; i < this.mElements.length; i++) {
            if (tmaxX != null) {
                let tintersect_maxX = this.getBB_intersect_maxX(this.mElements[i], tmaxX);
                if (tintersect_maxX != null)
                    this.translate(i, tintersect_maxX);
            }
        }
    }
    align_Bottom() {
        let tSel = this.mElements;
        if (tSel.length == 0)
            return;
        // get lowermost (z coordinate) point
        let tminZ = this.getBB_minZ(tSel);
        for (let i = 0; i < this.mElements.length; i++) {
            if (tminZ != null) {
                let tintersect_minZ = this.getBB_intersect_minZ(this.mElements[i], tminZ);
                if (tintersect_minZ != null)
                    this.translate(i, tintersect_minZ);
            }
        }
    }
    align_Top() {
        let tSel = this.mElements;
        if (tSel.length == 0)
            return;
        // get topmost (z coordinate) point
        let tmaxZ = this.getBB_maxZ(tSel);
        for (let i = 0; i < this.mElements.length; i++) {
            if (tmaxZ != null) {
                let tintersect_maxZ = this.getBB_intersect_maxZ(this.mElements[i], tmaxZ);
                if (tintersect_maxZ != null)
                    this.translate(i, tintersect_maxZ);
            }
        }
    }
    /**
     * pRotation and pRefPoint are given in world coordinates if pLocal = false, otherwise in
     * local coordinates.
     */
    rotate(pRotation, pLocal, pRefPoint = null) {
        for (let i = 0; i < this.mElements.length; ++i) {
            let tRefPoint = pRefPoint;
            let tRotation = pRotation;
            if (!pLocal && this.mInvWorldMatrix[i] != null) {
                let tAxisAngle = quaternionToAxisAngle(pRotation);
                let tAxis = new Vector3(tAxisAngle.x, tAxisAngle.y, tAxisAngle.z);
                rotateVector3ToRef(tAxis, this.mInvWorldRotation[i], tAxis);
                tRotation = Quaternion.RotationAxis(tAxis, tAxisAngle.w);
                if (tRefPoint != null)
                    tRefPoint = Vector3.TransformCoordinates(tRefPoint, this.mInvWorldMatrix[i]);
            }
            rotate(this.mElements[i].node, tRotation, pLocal, tRefPoint);
            updateFrozenWorldMatrix(this.mElements[i].node);
        }
        this.mApp.rendering.notifyModelInteraction();
        this.mApp.viewer.requestRenderFrame();
    }
    undoTransforms() {
        this.mApp.model.beginModification();
        for (let i = 0; i < this.mElements.length; ++i) {
            this.mElements[i].setTransform(this.mUndoTransforms[i]);
        }
        for (let i = 0; i < this.mElements.length; ++i) {
            if (this.mUndoData[i] != null)
                this.mElements[i].loadUndoData(UndoOperation.Transform, this.mUndoData[i]);
        }
        //this.mApp.model.eventEndTransformElements.trigger();
        this.mApp.model.endModification();
        this.mApp.viewer.requestRenderFrame();
    }
    getPositionX_order(pminX, pSelection) {
        let tDistances = new Array();
        let tRes = new Array();
        for (let tElement of pSelection) {
            let tE_BB = this.getBB_Points(tElement);
            let tBBCPos = this.getBB_CenterPoint(tE_BB);
            tBBCPos.z = 0;
            tBBCPos.y = 0;
            let tDist = Vector3.Distance(pminX, tBBCPos);
            tDistances.push(tDist);
        }
        // tslint:disable-next-line:only-arrow-functions
        tDistances.sort(function (a, b) { return a - b; });
        for (let tDist of tDistances) {
            for (let tElement of pSelection) {
                let tE_BB = this.getBB_Points(tElement);
                let tEPos = this.getBB_CenterPoint(tE_BB);
                tEPos.z = 0;
                tEPos.y = 0;
                let t_Dist = Vector3.Distance(pminX, tEPos);
                if (Util.numbersEqual(tDist, t_Dist)) {
                    tRes.push(tElement);
                    break;
                }
            }
        }
        return (tRes);
    }
    getPositionZ_order(pminZ, pSelection) {
        let tDistances = new Array();
        let tRes = new Array();
        for (let tElement of pSelection) {
            let tE_BB = this.getBB_Points(tElement);
            let tEPos = this.getBB_CenterPoint(tE_BB);
            tEPos.x = 0;
            tEPos.y = 0;
            let tDist = Vector3.Distance(pminZ, tEPos);
            tDistances.push(tDist);
        }
        // tslint:disable-next-line:only-arrow-functions
        tDistances.sort(function (a, b) { return a - b; });
        for (let tDist of tDistances) {
            for (let tElement of pSelection) {
                let tE_BB = this.getBB_Points(tElement);
                let tEPos = this.getBB_CenterPoint(tE_BB);
                tEPos.x = 0;
                tEPos.y = 0;
                let t_Dist = Vector3.Distance(pminZ, tEPos);
                if (Util.numbersEqual(tDist, t_Dist)) {
                    tRes.push(tElement);
                    break;
                }
            }
        }
        return (tRes);
    }
    getBB_Points(pSceneElements) {
        let tRes = new Array();
        let tWM = pSceneElements.node.getWorldMatrix();
        let tBB = pSceneElements.boundingBoxVisible;
        let tP0 = tBB.min.clone();
        tP0.y = 0;
        let tP2 = tBB.max.clone();
        tP2.y = 0;
        let tP1 = new Vector3(tP2.x, 0, tP0.z);
        let tP3 = new Vector3(tP0.x, 0, tP2.z);
        tP0 = Vector3.TransformCoordinates(tP0, tWM);
        tP1 = Vector3.TransformCoordinates(tP1, tWM);
        tP2 = Vector3.TransformCoordinates(tP2, tWM);
        tP3 = Vector3.TransformCoordinates(tP3, tWM);
        tRes.push(tP0, tP1, tP2, tP3);
        return (tRes);
    }
    getBB_CenterPoint(pBB_Points) {
        let tRes = Util.getCenterofPolygon(pBB_Points);
        return (tRes);
    }
    getBBcenter_minX(pSelection) {
        let tRes = new Vector3(1000000, 0, 0);
        for (let tElement of pSelection) {
            let tBB_points = this.getBB_Points(tElement);
            let tBBcenter = this.getBB_CenterPoint(tBB_points);
            if (tBBcenter.x < tRes.x) {
                tRes = tBBcenter.clone();
            }
        }
        return (tRes);
    }
    getBBcenter_minZ(pSelection) {
        let tRes = new Vector3(0, 0, 1000000);
        for (let tElement of pSelection) {
            let tBB_points = this.getBB_Points(tElement);
            let tBBcenter = this.getBB_CenterPoint(tBB_points);
            if (tBBcenter.z < tRes.z) {
                tRes = tBBcenter.clone();
            }
        }
        return (tRes);
    }
    getBBcenter_maxX(pSelection) {
        let tRes = new Vector3(-1000000, 0, 0);
        for (let tElement of pSelection) {
            let tBB_points = this.getBB_Points(tElement);
            let tBBcenter = this.getBB_CenterPoint(tBB_points);
            if (tBBcenter.x > tRes.x) {
                tRes = tBBcenter.clone();
            }
        }
        return (tRes);
    }
    getBBcenter_maxZ(pSelection) {
        let tRes = new Vector3(0, 0, -1000000);
        for (let tElement of pSelection) {
            let tBB_points = this.getBB_Points(tElement);
            let tBBcenter = this.getBB_CenterPoint(tBB_points);
            if (tBBcenter.z > tRes.z) {
                tRes = tBBcenter.clone();
            }
        }
        return (tRes);
    }
    getBB_minZ(pSelection) {
        let tRes = new Vector3(0, 0, 1000000);
        for (let tElement of pSelection) {
            let tBB_points = this.getBB_Points(tElement);
            for (let tP of tBB_points) {
                if (tP.z < tRes.z) {
                    tRes = tP.clone();
                }
            }
        }
        return (tRes);
    }
    getBB_maxZ(pSelection) {
        let tRes = new Vector3(0, 0, -1000000);
        for (let tElement of pSelection) {
            let tBB_points = this.getBB_Points(tElement);
            for (let tP of tBB_points) {
                if (tP.z > tRes.z) {
                    tRes = tP.clone();
                }
            }
        }
        return (tRes);
    }
    getBB_minX(pSelection) {
        let tRes = new Vector3(1000000, 0, 0);
        for (let tElement of pSelection) {
            let tBB_points = this.getBB_Points(tElement);
            for (let tP of tBB_points) {
                if (tP.x < tRes.x) {
                    tRes = tP.clone();
                }
            }
        }
        return (tRes);
    }
    getBB_maxX(pSelection) {
        let tRes = new Vector3(-1000000, 0, 0);
        for (let tElement of pSelection) {
            let tBB_points = this.getBB_Points(tElement);
            for (let tP of tBB_points) {
                if (tP.x > tRes.x) {
                    tRes = tP.clone();
                }
            }
        }
        return (tRes);
    }
    getBB_centerZ(pSelection) {
        let tmaxZ = this.getBB_maxZ(pSelection);
        let tminZ = this.getBB_minZ(pSelection);
        if (tmaxZ == null || tminZ == null)
            return (null);
        return (Vector3.Center(tmaxZ, tminZ));
    }
    getBB_centerX(pSelection) {
        let tmaxX = this.getBB_maxX(pSelection);
        let tminX = this.getBB_minX(pSelection);
        if (tmaxX == null || tminX == null)
            return (null);
        return (Vector3.Center(tmaxX, tminX));
    }
    getBB_intersect_minX(pElement, pIntersectPoint) {
        const tXdir = VECTOR3_X;
        let tRes = null;
        let tBB_points = this.getBB_Points(pElement);
        let tDist = 0;
        for (let tP of tBB_points) {
            let t_P = Util.to2d(tP);
            let t_P_Dir = Util.to2d(tP.subtract(tXdir));
            let tIP = Util.to2d(pIntersectPoint);
            let tIP_Dir = Util.to2d(pIntersectPoint.add(VECTOR3_Z));
            let tIS = Util.getIntersectionPointV2(t_P, t_P_Dir, tIP, tIP_Dir, false);
            if (tIS != null) {
                if (tRes == null) {
                    tRes = Util.to3d(tIS).subtract(tP);
                    tDist = Vector3.Distance(tP, Util.to3d(tIS));
                }
                else if (Vector3.Distance(tP, Util.to3d(tIS)) < tDist) {
                    tRes = Util.to3d(tIS).subtract(tP);
                    tDist = Vector3.Distance(tP, Util.to3d(tIS));
                }
            }
        }
        return (tRes);
    }
    getBB_intersect_maxX(pElement, pIntersectPoint) {
        const tXdir = VECTOR3_X;
        let tRes = null;
        let tBB_points = this.getBB_Points(pElement);
        let tDist = 0;
        for (let tP of tBB_points) {
            let t_P = Util.to2d(tP);
            let t_P_Dir = Util.to2d(tP.add(tXdir));
            let tIP = Util.to2d(pIntersectPoint);
            let tIP_Dir = Util.to2d(pIntersectPoint.add(VECTOR3_Z));
            let tIS = Util.getIntersectionPointV2(t_P, t_P_Dir, tIP, tIP_Dir, false);
            if (tIS != null) {
                if (tRes == null) {
                    tRes = Util.to3d(tIS).subtract(tP);
                    tDist = Vector3.Distance(tP, Util.to3d(tIS));
                }
                else if (Vector3.Distance(tP, Util.to3d(tIS)) < tDist) {
                    tRes = Util.to3d(tIS).subtract(tP);
                    tDist = Vector3.Distance(tP, Util.to3d(tIS));
                }
            }
        }
        return (tRes);
    }
    getBB_intersect_minZ(pElement, pIntersectPoint) {
        const tZdir = VECTOR3_Z;
        let tRes = null;
        let tBB_points = this.getBB_Points(pElement);
        let tDist = 0;
        for (let tP of tBB_points) {
            let t_P = Util.to2d(tP);
            let t_P_Dir = Util.to2d(tP.subtract(tZdir));
            let tIP = Util.to2d(pIntersectPoint);
            let tIP_Dir = Util.to2d(pIntersectPoint.add(VECTOR3_X));
            let tIS = Util.getIntersectionPointV2(t_P, t_P_Dir, tIP, tIP_Dir, false);
            if (tIS != null) {
                if (tRes == null) {
                    tRes = Util.to3d(tIS).subtract(tP);
                    tDist = Vector3.Distance(tP, Util.to3d(tIS));
                }
                else if (Vector3.Distance(tP, Util.to3d(tIS)) < tDist) {
                    tRes = Util.to3d(tIS).subtract(tP);
                    tDist = Vector3.Distance(tP, Util.to3d(tIS));
                }
            }
        }
        return (tRes);
    }
    getBB_intersect_maxZ(pElement, pIntersectPoint) {
        const tZdir = VECTOR3_Z;
        let tRes = null;
        let tBB_points = this.getBB_Points(pElement);
        let tDist = 0;
        for (let tP of tBB_points) {
            let t_P = Util.to2d(tP);
            let t_P_Dir = Util.to2d(tP.add(tZdir));
            let tIP = Util.to2d(pIntersectPoint);
            let tIP_Dir = Util.to2d(pIntersectPoint.add(VECTOR3_X));
            let tIS = Util.getIntersectionPointV2(t_P, t_P_Dir, tIP, tIP_Dir, false);
            if (tIS != null) {
                if (tRes == null) {
                    tRes = Util.to3d(tIS).subtract(tP);
                    tDist = Vector3.Distance(tP, Util.to3d(tIS));
                }
                else if (Vector3.Distance(tP, Util.to3d(tIS)) < tDist) {
                    tRes = Util.to3d(tIS).subtract(tP);
                    tDist = Vector3.Distance(tP, Util.to3d(tIS));
                }
            }
        }
        return (tRes);
    }
    getBB_intersect_centerX(pElement, pIntersectPoint) {
        const tXdir = VECTOR3_X;
        let tRes = null;
        let tBB_points = this.getBB_Points(pElement);
        let tcenterX = this.getBB_CenterPoint(tBB_points);
        let tDist = 0;
        let t_P = Util.to2d(tcenterX);
        let t_P_Dir = Util.to2d(tcenterX.add(tXdir));
        let tIP = Util.to2d(pIntersectPoint);
        let tIP_Dir = Util.to2d(pIntersectPoint.add(VECTOR3_Z));
        let tIS = Util.getIntersectionPointV2(t_P, t_P_Dir, tIP, tIP_Dir, false);
        if (tIS != null) {
            if (tRes == null) {
                tRes = Util.to3d(tIS).subtract(tcenterX);
                tDist = Vector3.Distance(tcenterX, Util.to3d(tIS));
            }
            else if (Vector3.Distance(tcenterX, Util.to3d(tIS)) < tDist) {
                tRes = Util.to3d(tIS).subtract(tcenterX);
                tDist = Vector3.Distance(tcenterX, Util.to3d(tIS));
            }
        }
        return (tRes);
    }
    getBB_intersect_centerZ(pElement, pIntersectPoint) {
        const tZXdir = VECTOR3_Z;
        let tRes = null;
        let tBB_points = this.getBB_Points(pElement);
        let tcenterX = this.getBB_CenterPoint(tBB_points);
        let tDist = 0;
        let t_P = Util.to2d(tcenterX);
        let t_P_Dir = Util.to2d(tcenterX.add(tZXdir));
        let tIP = Util.to2d(pIntersectPoint);
        let tIP_Dir = Util.to2d(pIntersectPoint.add(VECTOR3_X));
        let tIS = Util.getIntersectionPointV2(t_P, t_P_Dir, tIP, tIP_Dir, false);
        if (tIS != null) {
            if (tRes == null) {
                tRes = Util.to3d(tIS).subtract(tcenterX);
                tDist = Vector3.Distance(tcenterX, Util.to3d(tIS));
            }
            else if (Vector3.Distance(tcenterX, Util.to3d(tIS)) < tDist) {
                tRes = Util.to3d(tIS).subtract(tcenterX);
                tDist = Vector3.Distance(tcenterX, Util.to3d(tIS));
            }
        }
        return (tRes);
    }
}
export var AlignMode;
(function (AlignMode) {
    AlignMode[AlignMode["Top"] = 0] = "Top";
    AlignMode[AlignMode["Bottom"] = 1] = "Bottom";
    AlignMode[AlignMode["Left"] = 2] = "Left";
    AlignMode[AlignMode["Right"] = 3] = "Right";
    AlignMode[AlignMode["CenterH"] = 4] = "CenterH";
    AlignMode[AlignMode["CenterV"] = 5] = "CenterV";
    AlignMode[AlignMode["DistributeH"] = 6] = "DistributeH";
    AlignMode[AlignMode["DistributeV"] = 7] = "DistributeV";
})(AlignMode || (AlignMode = {}));
